import { Dispatch } from 'redux'
import {
  FileMalwareScan,
  EditionType,
} from '@graphics/gfx-common/dist/constants'
import {
  IFile,
  SetPreviewFieldWrapperType,
  OnProgressCallbackType,
  ICreateTmpLinkResponse,
} from '../types/file'
import {
  IPackInput,
  PreviewOptions,
  IEditionMetadataInput,
} from '../types/pack'
import { IMalwareScanResponse } from '../types/datafile'
import { triggerNotification } from '../actions/notifications'
import { IArchiveItem } from '../selectors/pack'
import { trace } from './common'
import { baseUrl, safePost, safeGet, safeDelete } from './http'
import { getPreviewImages } from './pack'
import {
  POLLING_TIMEOUT,
  POLLING_INTERVAL,
  ALLOWED_ARCHIVE_EXTENSION,
  DEFAULT_PASTED_IMAGE_NAME,
  PASTED_IMAGE_REPLACEMENT_NAME,
  PASTED_IMAGE_REPLACEMENT_EXT,
} from '../config/file-upload'

export const fileUrl = `${baseUrl}/file-uploads`
export const fileDownloadUrl = `${baseUrl}/file-downloads`

export const createTmpLink = (
  fileName: string
): Promise<ICreateTmpLinkResponse> => safePost(fileUrl, { fileName })

export const fileSizeConverter = (bytes: number): string => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  if (bytes === 0) {
    return '0 Byte'
  }
  const exponent = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${+(bytes / Math.pow(1024, exponent)).toFixed(1)} ${sizes[exponent]}`
}

export const fileSizeToMB = (bytes: number): number =>
  Math.floor(bytes / Math.pow(10, 6))

interface INameExt {
  name: string
  extension: string
}
export const splitFileNameExt = (name: string): INameExt => {
  const parts = name.indexOf('.') === -1 ? [name, ''] : name.split('.')
  return {
    name: parts.slice(0, -1).join('.'),
    extension: parts[parts.length - 1],
  }
}

export const formatFileName = (fileName: string, maxLength = 20): string => {
  const { name, extension } = splitFileNameExt(fileName)
  const formattedFileName = `${name.slice(
    0,
    maxLength - 3 - extension.length
  )}...${extension}`
  return fileName.length <= maxLength ? fileName : formattedFileName
}

export const parseXhrResponse = (
  responseText: any,
  defaultMessage = ''
): string => {
  if (typeof responseText !== 'string') {
    return defaultMessage
  }
  const [, message = defaultMessage] =
    responseText.match(/<Message>(.*)<\/Message>/) || []
  return message
}

export const uploadWithProgress = (
  url: string,
  file: File,
  onProgress: OnProgressCallbackType
): Promise<any> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.upload.addEventListener('progress', (e): void => {
      if (e.lengthComputable) {
        const progress = e.loaded / e.total
        onProgress(progress)
      }
    })
    xhr.onreadystatechange = (e): void => {
      const { readyState, status, responseText } = xhr
      if (readyState === 4) {
        if (status === 200) {
          resolve(e)
        } else {
          trace('failure', e)
          reject({
            status,
            message: parseXhrResponse(
              responseText,
              'Error while uploading the file'
            ),
          })
        }
      }
    }
    xhr.open('PUT', url, true)
    xhr.send(file)
  })

export const getFileById = (id: string) => safeGet(`${fileUrl}/${id}`)

export const deleteFileById = (id: string) => safeDelete(`${fileUrl}/${id}`)

export const extractFileName = (file: IFile): string | undefined =>
  (file.existingEdition && file.existingEdition.file.fileName) ||
  (file.fileInstance && file.fileInstance.name)

export const doExtensionsMatch = (file: IFile): boolean =>
  !!file.fileInstance &&
  !!file.existingEdition &&
  file.extension.toLowerCase() ===
    splitFileNameExt(file.existingEdition.file.fileName).extension.toLowerCase()

export enum UpdateOptions {
  ALL_FOLDERS,
  NEW_FOLDERS,
  SELECTED_FOLDERS,
}

export const calculateInitialValuesForUploadModalForm = (
  file: IFile,
  relatedEditions: IArchiveItem[]
) => ({
  fileName: file.name,
  archiveUpdateOptions:
    file.existingEdition && file.existingEdition.exact
      ? UpdateOptions.SELECTED_FOLDERS
      : UpdateOptions.ALL_FOLDERS,
  archiveFilesToUpdate:
    file.existingEdition && file.existingEdition.exact
      ? relatedEditions.map((item) =>
          item.id === (file.existingEdition && file.existingEdition.id)
            ? { ...item, value: true }
            : item
        )
      : relatedEditions,
})

export const filterEditionToUpdateByOption = (option: UpdateOptions) => {
  switch (option) {
    case UpdateOptions.ALL_FOLDERS:
      return () => true
    case UpdateOptions.SELECTED_FOLDERS:
      return ({ value }: { value: boolean }): boolean => !!value
    case UpdateOptions.NEW_FOLDERS:
    default:
      return () => false
  }
}

export const validateFileInstance = (
  fileInstance: File | null,
  maxFileSizeMb: number,
  allowedExtensions: string[],
  editionTypes: EditionType[]
): string[] => {
  if (!fileInstance) {
    return ['There is no file instance']
  }
  const fileSizeMb = fileSizeToMB(fileInstance.size)
  const { extension } = splitFileNameExt(fileInstance.name)
  const fileExtension = extension.toLowerCase()
  const isSingleFile = fileExtension !== ALLOWED_ARCHIVE_EXTENSION
  const isFileSizeNotAllowed = fileSizeMb >= maxFileSizeMb
  const isSingleFileNotAllowed =
    isSingleFile && !editionTypes.includes(EditionType.GraphicFile)
  const isFolderNotAllowed =
    !isSingleFile && !editionTypes.includes(EditionType.GraphicFolder)
  const isExtensionNotAllowed =
    isSingleFile &&
    !allowedExtensions.some(
      (item) => item.toLowerCase() === `.${fileExtension}`
    )
  return [
    isFileSizeNotAllowed &&
      // tslint:disable-next-line: max-line-length
      `The file(s) that you are attempting to upload exceeds the maximum filesize of ${maxFileSizeMb} Mb. Please try with an alternative.`,
    (isSingleFileNotAllowed || isFolderNotAllowed) &&
      'This file type is not allowed. Please try another one.',
    isExtensionNotAllowed &&
      `The file with extension .${fileExtension} is not allowed. Please try another one.`,
    fileInstance.size === 0 && 'The file is empty, please select another one.',
  ].filter(Boolean) as string[]
}

export const createPreviewFieldSetter = (
  setFieldValue: (field: string, value: string) => void,
  prefix: string = ''
): SetPreviewFieldWrapperType => (newTmpPreview) => (fileId) => {
  // TODO: #943 check if previewImages !== null @Andrew
  setFieldValue(`${prefix}previewImages.newFileId`, fileId)
  setFieldValue(`${prefix}previewImages.newTmpPreview`, newTmpPreview)
}

export const getNewTmpPreview = (
  entity?: IPackInput | IEditionMetadataInput
): string | undefined =>
  (entity?.useGraphicPreview === PreviewOptions.useUploadedPreview &&
    getPreviewImages(entity).newTmpPreview) ||
  undefined

export const readUploadedFile = (
  file: File | null,
  dispatch: Dispatch
): Promise<string | ArrayBuffer | null> =>
  new Promise((resolve, reject) => {
    const fileReader: FileReader = new FileReader()
    const fileReaderError = 'Problem parsing input file.'
    fileReader.onerror = () => {
      fileReader.abort()
      reject(dispatch(triggerNotification(fileReaderError)))
    }
    fileReader.onloadend = () => resolve(fileReader.result)
    if (!file) {
      reject(dispatch(triggerNotification(fileReaderError)))
    } else {
      fileReader.readAsDataURL(file)
    }
  })

export const forceDownloadFile = (downloadURI: string): void => {
  const a = document.createElement('a')
  a.style.display = 'none'
  document.body.appendChild(a)
  a.href = downloadURI
  a.setAttribute('download', 'fileName')
  a.setAttribute('target', '__blank')
  a.click()
  document.body.removeChild(a)
}

export const parseFileDownloadResponse = (
  { malwareScan, downloadURI, location }: IMalwareScanResponse,
  startTime: number
): void | string => {
  try {
    if (malwareScan === FileMalwareScan.Passed && downloadURI) {
      forceDownloadFile(downloadURI)
    } else if (malwareScan === FileMalwareScan.InProgress && location) {
      if (Date.now() - startTime >= POLLING_TIMEOUT) {
        return 'Antivirus scanning is taking longer than expected. Please try again later.'
      } else {
        setTimeout(() => {
          polling(location, startTime)
        }, POLLING_INTERVAL)
      }
    } else if (malwareScan === FileMalwareScan.Failed) {
      return 'A virus was found in a file. The file was deleted.'
    }
  } catch (err) {
    trace(err)
    return err.message
  }
}

export const polling = async (
  location: string,
  startTime: number
): Promise<void> => {
  const response = (await safeGet(location)) as IMalwareScanResponse
  parseFileDownloadResponse(response, startTime)
}

export const startFileDownload = async (
  fileId: string
): Promise<void | string> => {
  const response = (await safePost(fileDownloadUrl, {
    fileId,
  })) as IMalwareScanResponse
  return await parseFileDownloadResponse(response, Date.now())
}

// By default pasted image has name as 'image.png', it is readonly prop
export const replacePastedImageName = (
  filename: string,
  subsitutedExtension?: string
): string =>
  filename === DEFAULT_PASTED_IMAGE_NAME
    ? `${PASTED_IMAGE_REPLACEMENT_NAME} ${Date.now()}.${
        subsitutedExtension || PASTED_IMAGE_REPLACEMENT_EXT
      }`
    : filename
